import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import { Link02Icon, UserAdd01Icon } from "@hugeicons-pro/core-stroke-rounded";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { Inline } from "components/layout/reusableLayouts";

/**
 * The BYOC-only dashboard's navbar shortcuts.
 *
 * Both actions hand off to the page that owns the flow rather than hosting it
 * here. "Add user" sends you to the Users table with `?add=true`, which opens its
 * add-user drawer. It is the same drawer, but owned by the page that lists the
 * result, so a created user appears in the table behind it.
 *
 * "Connect program" links a card account rather than opening Plaid directly. The
 * Accounts page owns that flow end to end, including the card-to-user mapping
 * drawer that has to follow a successful link, so starting Plaid from here would
 * strand the user halfway through. That page is the Linked Accounts page, which
 * BYOC-only companies get at /admin/accounts rather than under Settings.
 */
const QuickActions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);

  if (!isAdminOrHigher) return null;

  const actions: {
    icon: IconSvgElement;
    label: string;
    onClick: () => void;
  }[] = [
    {
      icon: UserAdd01Icon,
      label: t("Add user"),
      onClick: () => navigate("/admin/users?add=true"),
    },
    {
      icon: Link02Icon,
      label: t("Connect program"),
      onClick: () => navigate("/admin/accounts"),
    },
  ];

  return (
    <Inline gap="var(--space-3)" wrap={false}>
      {actions.map((action) => (
        <Button
          key={action.label}
          variant="default-outlined"
          startIcon={
            <HugeiconsIcon icon={action.icon} size={18} color="currentColor" />
          }
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ))}
    </Inline>
  );
};

export default QuickActions;
